import React, { useEffect, useRef } from "react";
import { Spine } from "pixi-spine";
import { Container } from "@pixi/react";
import { getAsset } from "../../assets";
import { useSelector, useDispatch } from "react-redux";
import { WIN_CATEGORIES } from "../../settings";
import { playSound, stopSound } from "../../State/Slices/soundsSlice";
import { SOUND_NAME } from "../../sounds";
import {
  setBigWinAnimationStarted,
  setBigWinAnimationCompleted,
} from "../../State/Slices/animationsSlice";

const SpineWithStages = ({
  animationPath,
  winName,
  x,
  y,
  zIndex = 0,
  symbolSize,
}) => {
  const containerRef = useRef(null);
  const spineAnimRef = useRef(null);
  const currentSoundRef = useRef(null);
  const timeoutRef = useRef(null);
  const dispatch = useDispatch();
  const { status } = useSelector((state) => state.assets);

  const getAnimationSequence = (winName) => {
    const currentIndex = WIN_CATEGORIES.findIndex(
      (cat) => cat.name === winName
    );
    if (currentIndex === -1) return [];
    return WIN_CATEGORIES.filter((_, index) => index <= currentIndex)
      .filter((cat) => cat.level)
      .map((cat) => ({
        level: cat.level,
        name: cat.name,
        duration: cat.duration,
      }));
  };

  const playStageAnimation = (spineAnim, levels, currentIndex = 0) => {
    if (currentIndex >= levels.length) {
      dispatch(setBigWinAnimationCompleted(true));
      dispatch(setBigWinAnimationStarted(false));
      return;
    }

    const { level, name, duration } = levels[currentIndex];

    const playAnimation = (stage) => {
      const stageName = `${level}_${stage}`;
      if (!spineAnim.state.hasAnimation(stageName)) {
        console.warn(`Animation "${stageName}" is missing!`);
        return;
      }

      if (stage === "In") {
        dispatch(playSound(SOUND_NAME[name]));
        currentSoundRef.current = SOUND_NAME[name];
      }

      const isLoop = stage === "Loop";
      spineAnim.state.setAnimation(0, stageName, isLoop);
      spineAnim.state.clearListeners();

      if (stage === "In") {
        spineAnim.state.addListener({
          complete: () => {
            playAnimation("Loop");
            timeoutRef.current = setTimeout(() => {
              playAnimation("Out");
            }, duration / 1.5);
          },
        });
      } else if (stage === "Out") {
        spineAnim.state.addListener({
          complete: () => {
            playStageAnimation(spineAnim, levels, currentIndex + 1);
          },
        });
      }
    };

    playAnimation("In");
  };

  useEffect(() => {
    const loadSpineAnimation = async () => {
      try {
        const spineResource = getAsset(status, animationPath);
        if (!spineResource?.spineData) {
          console.warn("Spine resource or data is missing!");
          return;
        }

        const spineAnim = new Spine(spineResource.spineData);
        spineAnimRef.current = spineAnim;

        spineAnim.position.set(0, 0);
        spineAnim.width = symbolSize * 1.45;
        spineAnim.height = symbolSize * 1.45;

        if (containerRef.current) {
          containerRef.current.addChild(spineAnim);

          const levels = getAnimationSequence(winName);
          playStageAnimation(spineAnim, levels);
        }
      } catch (error) {
        console.error("Error loading Spine animation:", error);
      }
    };

    loadSpineAnimation();
    dispatch(setBigWinAnimationStarted(true));
    dispatch(setBigWinAnimationCompleted(false));

    return () => {
      if (containerRef.current) containerRef.current.removeChildren();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      if (currentSoundRef.current) {
        dispatch(stopSound(currentSoundRef.current));
        currentSoundRef.current = null;
      }
    };
  }, [animationPath, dispatch, status, winName]);

  useEffect(() => {
    if (spineAnimRef.current) {
      const spineAnim = spineAnimRef.current;

      spineAnim.width = symbolSize * 1.45;
      spineAnim.height = symbolSize * 1.45;
    }
  }, [symbolSize]);

  return (
    <Container
      ref={containerRef}
      x={x}
      y={y}
      zIndex={zIndex}
      name={`Spine Win Animation - ${winName}`}
      sortChildren={true}
    />
  );
};

export default SpineWithStages;
